import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

function emptyLine() {
  return {
    item_id: "", item_name: "", hsn: "", unit: "gm",
    gross_weight: 0, net_weight: 0, gold_rate: 0,
    net_amount: 0, gst_percent: 3, total_amount: 0,
  };
}

function calcLine(l) {
  const net = Number(l.net_weight) || 0;
  const rate = Number(l.gold_rate) || 0;
  const netAmount = Number((net * rate).toFixed(2));
  const gst = Number(l.gst_percent) || 0;
  const total = Number((netAmount * (1 + gst / 100)).toFixed(2));
  return { ...l, net_amount: netAmount, total_amount: total };
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function VoucherCreate() {
  const { id } = useParams();
  const editing = !!id;
  const nav = useNavigate();

  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [nextNo, setNextNo] = useState({ travelling: "", delivery: "" });
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    voucher_type: "travelling",
    to_customer_id: "",
    to_customer_name: "",
    to_customer_address: "",
    to_customer_gst: "",
    salesman_id: "",
    salesman_name: "",
    voucher_date: todayIso(),
    vehicle_number: "",
    transport: "",
    travelling_persons: "",
    valid_from: todayIso(),
    valid_to: "",
    delivery_date: "",
    delivery_location: "",
    remarks: "",
    items: [emptyLine()],
  });

  useEffect(() => {
    (async () => {
      const [ri, rc, rs, rnt, rnd] = await Promise.all([
        api.get("/voucher/items"),
        api.get("/voucher/customers"),
        api.get("/voucher/salesmen"),
        api.get("/voucher/next-number/travelling"),
        api.get("/voucher/next-number/delivery"),
      ]);
      setItems(ri.data);
      setCustomers(rc.data);
      setSalesmen(rs.data);
      setNextNo({ travelling: rnt.data.next, delivery: rnd.data.next });
    })();
  }, []);

  useEffect(() => {
    if (!editing) return;
    api.get(`/voucher/vouchers/${id}`).then((r) => {
      setForm({ ...r.data, items: r.data.items?.length ? r.data.items : [emptyLine()] });
    });
  }, [editing, id]);

  const pickCustomer = (cid) => {
    if (!cid) {
      setForm((f) => ({ ...f, to_customer_id: "", to_customer_name: "", to_customer_address: "", to_customer_gst: "" }));
      return;
    }
    const c = customers.find((x) => x.id === cid);
    if (!c) return;
    const addr = [c.address, c.city, c.state, c.pincode].filter(Boolean).join(", ");
    setForm((f) => ({
      ...f, to_customer_id: c.id, to_customer_name: c.customer_name,
      to_customer_address: addr, to_customer_gst: c.gst || "",
    }));
  };

  const pickSalesman = (sid) => {
    if (!sid) {
      setForm((f) => ({ ...f, salesman_id: "", salesman_name: "" }));
      return;
    }
    const s = salesmen.find((x) => x.id === sid);
    if (!s) return;
    setForm((f) => ({ ...f, salesman_id: s.id, salesman_name: s.name }));
  };

  const updateLine = (idx, patch) => {
    setForm((f) => {
      const arr = f.items.slice();
      arr[idx] = calcLine({ ...arr[idx], ...patch });
      return { ...f, items: arr };
    });
  };

  const pickItem = (idx, iid) => {
    const it = items.find((x) => x.id === iid);
    if (!it) return;
    updateLine(idx, { item_id: it.id, item_name: it.item_name, hsn: it.hsn || "", unit: it.unit || "gm" });
  };

  const addLine = () => setForm((f) => ({ ...f, items: [...f.items, emptyLine()] }));
  const removeLine = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((a, x) => a + (Number(x.net_amount) || 0), 0);
    const gst = form.items.reduce((a, x) => a + ((Number(x.total_amount) || 0) - (Number(x.net_amount) || 0)), 0);
    const grand = form.items.reduce((a, x) => a + (Number(x.total_amount) || 0), 0);
    const gross = form.items.reduce((a, x) => a + (Number(x.gross_weight) || 0), 0);
    const net = form.items.reduce((a, x) => a + (Number(x.net_weight) || 0), 0);
    return { subtotal, gst, grand, gross, net };
  }, [form.items]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.to_customer_name.trim()) {
      toast.error("Please select or enter customer");
      return;
    }
    if (form.items.length === 0 || !form.items.some((i) => i.item_name.trim())) {
      toast.error("Add at least one item");
      return;
    }
    // Clean payload
    const payload = {
      ...form,
      items: form.items.filter((i) => i.item_name.trim()).map(calcLine),
    };
    // Blank dates should be null
    ["valid_from", "valid_to", "delivery_date"].forEach((k) => {
      if (!payload[k]) payload[k] = null;
    });
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/voucher/vouchers/${id}`, payload);
        toast.success("Voucher updated");
        nav(`/voucher/vouchers/${data.id}`);
      } else {
        const { data } = await api.post("/voucher/vouchers", payload);
        toast.success(`Voucher created: ${data.voucher_no}`);
        nav(`/voucher/vouchers/${data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save voucher");
    } finally {
      setSaving(false);
    }
  };

  const upcomingNo = editing ? form.voucher_no : (form.voucher_type === "travelling" ? nextNo.travelling : nextNo.delivery);

  return (
    <form onSubmit={submit} className="space-y-6 max-w-6xl" data-testid="voucher-form">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-zinc-500">Voucher ERP</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">
            {editing ? "Edit Voucher" : "New Voucher"}
          </h1>
          <div className="text-xs text-zinc-500 mt-1">
            Voucher No.: <span className="font-mono text-zinc-900" data-testid="voucher-next-no">{upcomingNo || "—"}</span>
          </div>
        </div>
        <Button type="submit" disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 h-10" data-testid="voucher-submit-button">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : (editing ? "Save Changes" : "Create Voucher")}
        </Button>
      </div>

      <Card className="p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg tracking-tight">Voucher Info</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label>Voucher Type *</Label>
            <Select value={form.voucher_type} onValueChange={(v) => setForm({ ...form, voucher_type: v })} disabled={editing}>
              <SelectTrigger data-testid="voucher-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="travelling">Travelling Voucher</SelectItem>
                <SelectItem value="delivery">Delivery Challan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Voucher Date *</Label>
            <Input type="date" required value={form.voucher_date} onChange={(e) => setForm({ ...form, voucher_date: e.target.value })} data-testid="voucher-date-input" />
          </div>
          {form.voucher_type === "travelling" ? (
            <>
              <div className="space-y-2">
                <Label>Valid From</Label>
                <Input type="date" value={form.valid_from || ""} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Valid To</Label>
                <Input type="date" value={form.valid_to || ""} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} />
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Delivery Date</Label>
                <Input type="date" value={form.delivery_date || ""} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Delivery Location</Label>
                <Input value={form.delivery_location} onChange={(e) => setForm({ ...form, delivery_location: e.target.value })} />
              </div>
            </>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Vehicle Number</Label>
            <Input value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value.toUpperCase() })} data-testid="vehicle-number-input" />
          </div>
          <div className="space-y-2">
            <Label>Transport</Label>
            <Input value={form.transport} onChange={(e) => setForm({ ...form, transport: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Salesman</Label>
            <Select value={form.salesman_id || "__none__"} onValueChange={(v) => pickSalesman(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="salesman-select"><SelectValue placeholder="Select salesman" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {salesmen.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {form.voucher_type === "travelling" && (
          <div className="space-y-2">
            <Label>Travelling Person(s)</Label>
            <Textarea rows={2} value={form.travelling_persons} onChange={(e) => setForm({ ...form, travelling_persons: e.target.value })} placeholder="Names of persons carrying goods" />
          </div>
        )}
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg tracking-tight">Customer (To)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Select from Master</Label>
            <Select value={form.to_customer_id || "__manual__"} onValueChange={(v) => pickCustomer(v === "__manual__" ? "" : v)}>
              <SelectTrigger data-testid="customer-select"><SelectValue placeholder="— Manual entry —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__manual__">— Manual entry —</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.customer_name} {c.company_name ? `(${c.company_name})` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Customer Name *</Label>
            <Input required value={form.to_customer_name} onChange={(e) => setForm({ ...form, to_customer_name: e.target.value })} data-testid="customer-name-input" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Address</Label>
            <Textarea rows={2} value={form.to_customer_address} onChange={(e) => setForm({ ...form, to_customer_address: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Customer GST</Label>
            <Input value={form.to_customer_gst} onChange={(e) => setForm({ ...form, to_customer_gst: e.target.value.toUpperCase() })} />
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg tracking-tight">Items</h3>
          <Button type="button" variant="outline" onClick={addLine} data-testid="add-item-line">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Line
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm hidden md:table" data-testid="voucher-item-table">
            <thead className="hidden md:table-header-group">
              <tr className="text-left text-zinc-500 border-b border-border">
                <th className="pb-2 overline">#</th>
                <th className="pb-2 overline">Item</th>
                <th className="pb-2 overline">HSN</th>
                <th className="pb-2 overline text-right">Gross (g)</th>
                <th className="pb-2 overline text-right">Net (g)</th>
                <th className="pb-2 overline text-right">Rate/g</th>
                <th className="pb-2 overline text-right">Amount</th>
                <th className="pb-2 overline text-right">GST%</th>
                <th className="pb-2 overline text-right">Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {form.items.map((l, idx) => (
                <tr key={idx} data-testid={`item-line-${idx}`}>
                  <td className="py-2 text-zinc-500">{idx + 1}</td>
                  <td className="py-2">
                    <div className="flex flex-col gap-1 min-w-[180px]">
                      <Select value={l.item_id || "__custom__"} onValueChange={(v) => v === "__custom__" ? updateLine(idx, { item_id: "" }) : pickItem(idx, v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Pick item" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__custom__">— Custom —</SelectItem>
                          {items.map((it) => (
                            <SelectItem key={it.id} value={it.id}>{it.item_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input value={l.item_name} onChange={(e) => updateLine(idx, { item_name: e.target.value })} placeholder="Item name" className="h-8 text-xs" data-testid={`item-name-${idx}`} />
                    </div>
                  </td>
                  <td className="py-2"><Input value={l.hsn} onChange={(e) => updateLine(idx, { hsn: e.target.value })} className="h-8 text-xs w-20" /></td>
                  <td className="py-2"><Input type="number" step="0.001" value={l.gross_weight} onChange={(e) => updateLine(idx, { gross_weight: e.target.value })} className="h-8 text-xs w-24 text-right" /></td>
                  <td className="py-2"><Input type="number" step="0.001" value={l.net_weight} onChange={(e) => updateLine(idx, { net_weight: e.target.value })} className="h-8 text-xs w-24 text-right" data-testid={`net-weight-${idx}`} /></td>
                  <td className="py-2"><Input type="number" step="0.01" value={l.gold_rate} onChange={(e) => updateLine(idx, { gold_rate: e.target.value })} className="h-8 text-xs w-24 text-right" data-testid={`gold-rate-${idx}`} /></td>
                  <td className="py-2 text-right num text-xs">{(l.net_amount || 0).toFixed(2)}</td>
                  <td className="py-2"><Input type="number" step="0.01" value={l.gst_percent} onChange={(e) => updateLine(idx, { gst_percent: e.target.value })} className="h-8 text-xs w-16 text-right" /></td>
                  <td className="py-2 text-right num text-xs font-semibold">{(l.total_amount || 0).toFixed(2)}</td>
                  <td className="py-2 text-right">
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => removeLine(idx)} disabled={form.items.length === 1}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Mobile card layout for items */}
          <div className="md:hidden space-y-3 mt-3" data-testid="mobile-item-list">
            {form.items.map((l, idx) => (
              <Card key={idx} className="p-3" data-testid={`mobile-item-${idx}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-medium">{idx + 1}. {l.item_name || "—"}</div>
                  <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => removeLine(idx)} disabled={form.items.length === 1}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-zinc-500">HSN:</span> <span className="num">{l.hsn || "—"}</span></div>
                  <div><span className="text-zinc-500">Net Wt:</span> <span className="num">{Number(l.net_weight || 0).toFixed(3)} g</span></div>
                  <div><span className="text-zinc-500">Rate/g:</span> <span className="num">₹ {Number(l.gold_rate || 0).toFixed(2)}</span></div>
                  <div><span className="text-zinc-500">Amount:</span> <span className="num">₹ {Number(l.net_amount || 0).toFixed(2)}</span></div>
                  <div><span className="text-zinc-500">GST%:</span> <span className="num">{Number(l.gst_percent || 0).toFixed(2)}%</span></div>
                  <div><span className="text-zinc-500">Total:</span> <span className="num font-semibold">₹ {Number(l.total_amount || 0).toFixed(2)}</span></div>
                </div>
                <div className="mt-2 pt-2 border-t border-border">
                  <Select value={l.item_id || "__custom__"} onValueChange={(v) => v === "__custom__" ? updateLine(idx, { item_id: "" }) : pickItem(idx, v)} className="w-full">
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Change item" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__custom__">— Custom —</SelectItem>
                      {items.map((it) => (
                        <SelectItem key={it.id} value={it.id}>{it.item_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input value={l.item_name} onChange={(e) => updateLine(idx, { item_name: e.target.value })} placeholder="Item name" className="h-8 text-xs mt-1" data-testid={`mobile-item-name-${idx}`} />
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Input value={l.hsn} onChange={(e) => updateLine(idx, { hsn: e.target.value })} className="h-8 text-xs" placeholder="HSN" />
                    <Input type="number" step="0.001" value={l.gross_weight} onChange={(e) => updateLine(idx, { gross_weight: e.target.value })} className="h-8 text-xs text-right" placeholder="Gross Wt" />
                    <Input type="number" step="0.001" value={l.net_weight} onChange={(e) => updateLine(idx, { net_weight: e.target.value })} className="h-8 text-xs text-right" placeholder="Net Wt" data-testid={`mobile-net-wt-${idx}`} />
                    <Input type="number" step="0.01" value={l.gold_rate} onChange={(e) => updateLine(idx, { gold_rate: e.target.value })} className="h-8 text-xs text-right" placeholder="Rate/g" data-testid={`mobile-rate-${idx}`} />
                    <Input type="number" step="0.01" value={l.gst_percent} onChange={(e) => updateLine(idx, { gst_percent: e.target.value })} className="h-8 text-xs text-right" placeholder="GST%" data-testid={`mobile-gst-${idx}`} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-4 border-t border-border">
          <div><div className="overline text-zinc-500">Gross Wt</div><div className="num font-semibold" data-testid="total-gross">{totals.gross.toFixed(3)} g</div></div>
          <div><div className="overline text-zinc-500">Net Wt</div><div className="num font-semibold" data-testid="total-net">{totals.net.toFixed(3)} g</div></div>
          <div><div className="overline text-zinc-500">Subtotal</div><div className="num font-semibold">₹ {totals.subtotal.toFixed(2)}</div></div>
          <div><div className="overline text-zinc-500">GST</div><div className="num font-semibold">₹ {totals.gst.toFixed(2)}</div></div>
          <div><div className="overline text-zinc-500">Grand Total</div><div className="num font-black text-lg" data-testid="total-grand">₹ {totals.grand.toFixed(2)}</div></div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg tracking-tight">Remarks</h3>
        <Textarea rows={3} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Any notes or remarks" />
      </Card>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => nav(-1)}>Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-zinc-900 hover:bg-zinc-800">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : (editing ? "Save Changes" : "Create Voucher")}
        </Button>
      </div>
    </form>
  );
}
